import { useLingui } from '@lingui/react';
import Head from 'expo-router/head';
import { Platform } from 'react-native';
import { useAppLocale } from '../lib/i18n/AppI18nProvider';
import { openGraphLocale } from '../lib/i18n/publicWeb';
import { resolvePageMetadata, type MetaTagsProps } from '../lib/i18n/resolvePageMetadata';

export default function MetaTags({ structuredDataInBody = false, ...props }: MetaTagsProps & { structuredDataInBody?: boolean }) {
    const { i18n } = useLingui();
    const { messageLocale } = useAppLocale();
    if (Platform.OS !== 'web') return null;
    const {
        fullTitle, emitProductionMetadata, resolvedCanonicalUrl, alternates,
        structuredData, structuredDataKind, resolvedDescription, resolvedKeywords,
        resolvedRobots, resolvedImage, resolvedPageUrl, type, surfaceId, locale,
        otherOpenGraphLocales,
    } = resolvePageMetadata(props, i18n, messageLocale);
    // Expo's static metadata API does not carry arbitrary scripts. Pages that
    // need JSON-LD in their initial HTML can render it outside the client Head.
    const structuredDataScripts = structuredData.map((value, index) => (
        <script key={`${structuredDataKind}-${index}`} type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(value).replace(/</g, '\\u003c') }} />
    ));
    return <>
        <Head>
            <title>{fullTitle}</title>
            {emitProductionMetadata && <link rel="canonical" href={resolvedCanonicalUrl} />}
            {alternates.map((alternate) => (
                <link
                    key={alternate.hrefLang}
                    rel="alternate"
                    hrefLang={alternate.hrefLang}
                    href={alternate.href}
                />
            ))}
            {!structuredDataInBody && structuredDataScripts}
            <meta name="description" content={resolvedDescription} />
            {resolvedKeywords && <meta name="keywords" content={resolvedKeywords} />}
            {resolvedRobots && <meta name="robots" content={resolvedRobots} />}

            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={resolvedDescription} />
            <meta property="og:image" content={resolvedImage} />
            {resolvedPageUrl && <meta property="og:url" content={resolvedPageUrl} />}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content="Paperzilla" />
            {surfaceId && <meta property="og:locale" content={openGraphLocale(locale)} />}
            {otherOpenGraphLocales.map((alternateLocale) => (
                <meta
                    key={alternateLocale}
                    property="og:locale:alternate"
                    content={alternateLocale}
                />
            ))}

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={resolvedDescription} />
            <meta name="twitter:image" content={resolvedImage} />
            {resolvedPageUrl && <meta name="twitter:url" content={resolvedPageUrl} />}
        </Head>
        {structuredDataInBody && structuredDataScripts}
    </>;
}
